import React, { useState } from "react";

import { Logo } from "../../../shared/components/Logo";
import { respondToNewPasswordChallenge } from "../client";

interface NewPasswordRequiredPageProps {
  session: string;
  username: string;
  onComplete: () => void;
}

export function NewPasswordRequiredPage({ session, username, onComplete }: NewPasswordRequiredPageProps): JSX.Element {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!newPassword) {
      setError("Please enter a new password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await respondToNewPasswordChallenge(session, username, newPassword);
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set new password.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-page auth-page-signin" aria-labelledby="newpw-title">
      <section className="auth-card">
        <div className="auth-logo-area">
          <Logo size={40} />
          <span className="auth-logo-text">Q<span className="auth-logo-accent">picks</span></span>
        </div>

        <h1 id="newpw-title" className="auth-title">Set new password</h1>
        <p className="auth-subtitle">
          Your account requires a new password. Please choose a password to continue.
        </p>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label htmlFor="newpw-password" className="auth-label">New Password</label>
          <input
            id="newpw-password"
            className="auth-input"
            type="password"
            placeholder="••••••••"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={submitting}
          />

          <label htmlFor="newpw-confirm" className="auth-label">Confirm Password</label>
          <input
            id="newpw-confirm"
            className="auth-input"
            type="password"
            placeholder="••••••••"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={submitting}
          />

          <button type="submit" className="auth-primary-button" disabled={submitting}>
            {submitting ? "Setting password..." : "Set Password & Sign In"}
          </button>
        </form>

        {error && (
          <p role="alert" className="auth-error">{error}</p>
        )}
      </section>
    </main>
  );
}
